import AddIcon from '@mui/icons-material/Add';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { alpha } from '@mui/material/styles';
import React, { type JSX, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { mutate } from 'swr';

import { UserContext } from '~/auth/userContext';
import { HeaderField } from '~/components/common/HeaderField';
import Skeleton from '~/components/common/Skeleton';
import { DownloadStampedInvoiceDropdownMenu } from '~/components/invoices/stampedInvoice';
import { UploadDocumentType } from '~/components/uploadDocument/AddManuallyDropdown';
import type { LiftAIDocumentTagged } from '~/components/uploadDocument/SelectDocumentTypeDialog';
import SelectUploadDocumentTypeDialog from '~/components/uploadDocument/SelectDocumentTypeDialog';
import type { LiftAIDocumentUntagged } from '~/components/uploadDocument/UploadDocumentDialogFlow';
import { LiftAITextLink } from '~/components/utils/helpers';
import { getInvoiceMutator } from '~/data/hooks/utils';
import { DocumentUploadSource } from '~/data/schemas/documentReview';
import type { AdjustmentInvoice, Invoice } from '~/data/schemas/invoice';
import {
  InvoiceKind,
  invoiceRejectionCategoryToLabelMap,
  InvoiceStatus,
  invoiceStatusToColorMap,
  invoiceStatusToLabelMap,
  isInvoiceFinal,
} from '~/data/schemas/invoice';
import { uploadAttachment } from '~/data/uploadAttachment';
import { BUTTON_SX, CARD_SX } from '~/theme';
import { formatCurrency, toLongDateFormat } from '~/utils/format';
import { buildUrlForInvoices } from '~/utils/urlBuilder';

/**
 * Only `DecisionLayout` below still builds on this. The drawer is gone, and the
 * `InvoiceOverview` component that took a `layout` prop no longer has a caller — see
 * the note on it.
 */
type InvoiceOverviewLayout = 'drawer' | 'page';

type IInvoiceOverviewField = {
  /** The label of the field. */
  label: string;
  /** The name of the field. */
  name: keyof Invoice | 'stampedInvoice';
  /** The value of the field, if any. */
  value?: string;
  /** The caption of the field, if any. Null when the API omits it (e.g. a
   * property with no address serializes `address: null`). */
  caption?: string | null;
  /** The default value of the field, if `value` is falsy. */
  defaultValue?: string;
  /** Whether the field should take up the full width of its container. */
  fullWidth?: boolean;
} & (
  | { type: 'link'; uri: string | undefined; element?: never }
  | { type: 'text'; uri?: never; element?: never }
  | { type: 'custom'; uri?: never; element: () => JSX.Element }
);

const sectionAccordionSx = {
  boxShadow: 'none',
  '&::before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
} as const;

/** The Overview's unit of structure: a bordered white card with a heading, an optional chip beside it and an optional action at its end. */
const OverviewCard = ({
  title,
  chip,
  action,
  children,
}: {
  title: string;
  chip?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      ...CARD_SX,
      p: 2,
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {chip}
      {action ? (
        <>
          <Box flexGrow={1} />
          {action}
        </>
      ) : null}
    </Stack>
    {children}
  </Box>
);

const DetailField = ({
  label,
  value,
  span = 6,
  breakAll = false,
}: {
  label: string;
  value: string;
  span?: number;
  breakAll?: boolean;
}) => (
  <Grid size={{ xs: 12, sm: span }} mb={1}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={breakAll ? { wordBreak: 'break-all' } : undefined}>
      {value}
    </Typography>
  </Grid>
);

/** How the invoice got here: a direct upload, or an ingested email. */
export const SourceSection = ({
  invoice,
  variant = 'card',
}: {
  invoice: Invoice;
  variant?: 'card' | 'field';
}) => {
  const source = invoice.source ?? DocumentUploadSource.Manual;
  const email = invoice.emailContext;

  // In the header this is provenance, not content: one line saying where the invoice
  // came from, in the same shape as Property and Type beside it.
  if (variant === 'field') {
    const isEmail = source !== DocumentUploadSource.Manual;
    const who = isEmail ? email?.sender : invoice.uploadedBy?.fullName;
    const when = isEmail ? email?.receivedAt : invoice.uploadedAt;

    return (
      <HeaderField
        label="Source"
        caption={[who, when ? toLongDateFormat(when) : null].filter(Boolean).join(' · ') || 'N/A'}
      >
        <Typography variant="body2">{isEmail ? 'Email Ingestion' : 'Direct Upload'}</Typography>
      </HeaderField>
    );
  }

  if (source === DocumentUploadSource.Manual) {
    return (
      <OverviewCard
        title="Source"
        chip={
          <Chip
            icon={<UploadFileOutlined fontSize="small" />}
            label="Direct Upload"
            size="small"
            variant="outlined"
          />
        }
      >
        <Grid container columnSpacing={2}>
          <DetailField label="Uploaded by" value={invoice.uploadedBy?.fullName ?? 'N/A'} />
          <DetailField
            label="Uploaded on"
            value={invoice.uploadedAt ? toLongDateFormat(invoice.uploadedAt) : 'N/A'}
          />
        </Grid>
      </OverviewCard>
    );
  }

  // Email source
  return (
    <OverviewCard
      title="Source"
      chip={
        <Chip
          icon={<EmailOutlined fontSize="small" />}
          label="Email Ingestion"
          size="small"
          variant="outlined"
          color="info"
        />
      }
    >
      <Grid container columnSpacing={2}>
        <DetailField label="From" value={email?.sender ?? 'N/A'} breakAll />
        <DetailField
          label="Received on"
          value={email?.receivedAt ? toLongDateFormat(email.receivedAt) : 'N/A'}
        />
        <DetailField label="Subject" value={email?.subject ?? 'N/A'} span={12} />
      </Grid>
      {email?.textBody ? (
        <Accordion disableGutters elevation={0} square sx={sectionAccordionSx}>
          <AccordionSummary
            sx={{
              px: 0,
              minHeight: 40,
              '& .MuiAccordionSummary-content': { my: 1 },
              // Chevron leads the label, as in the Related sections, so it rotates from here
              // rather than through MUI's trailing `expandIcon` slot.
              '& .chevron': { transition: 'transform 150ms' },
              '&.Mui-expanded .chevron': { transform: 'rotate(180deg)' },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <ExpandMore className="chevron" sx={{ color: 'action.active' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Message
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {email.textBody}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </OverviewCard>
  );
};

const afterReviewField = (reviewedAmount: number, savings: number | null, currencyCode: string) =>
  ({
    label: 'After Review',
    name: 'reviewedAmount',

    value:
      formatCurrency({ value: reviewedAmount, currencyCode }) +
      (savings ? ` (${formatCurrency({ value: savings, currencyCode })} Savings)` : ''),

    type: 'text',
  }) as IInvoiceOverviewField;

export const getDecisionFields = (
  invoice: Invoice,
  currencyCode: string,
): IInvoiceOverviewField[] => {
  const fields: IInvoiceOverviewField[] = [
    {
      label: 'Date Stamped',
      name: 'dateStamped',
      value: invoice.dateStamped ? toLongDateFormat(invoice.dateStamped) : 'N/A',
      type: 'text',
    },
    {
      label: 'Stamped By',
      name: 'consultant',
      value: invoice.consultant?.fullName ?? 'N/A',
      type: 'text',
    },
  ];

  const reasonField: IInvoiceOverviewField = {
    label: 'Reason',
    name: 'reason',
    value: invoice.reason ?? 'N/A',
    type: 'text',
    fullWidth: true,
  };

  const rejectionCategoryField: IInvoiceOverviewField = {
    label: 'Rejection Category',
    name: 'rejectionCategory',
    value:
      invoice.rejectionCategory && invoice.rejectionCategory.length > 0
        ? invoice.rejectionCategory
            .map((category) => invoiceRejectionCategoryToLabelMap.get(category) ?? '')
            .join(', ')
        : 'N/A',
    type: 'text',
    fullWidth: true,
  };

  const downloadField: IInvoiceOverviewField = {
    label: invoice.kind === InvoiceKind.Proposal ? 'Stamped Proposal' : 'Stamped Invoice',
    name: 'stampedInvoice',
    value: 'DOWNLOAD',
    type: 'custom',
    element: () => <DownloadStampedInvoiceDropdownMenu invoice={invoice} />,
  };

  const savings: number | null = isInvoiceFinal(invoice) ? (invoice.savings ?? 0) : null;

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (invoice.status) {
    case InvoiceStatus.Approved:
      return [...fields, downloadField];

    case InvoiceStatus.Rejected:
      return [
        ...fields,
        afterReviewField(0, savings, currencyCode),
        downloadField,
        rejectionCategoryField,
        reasonField,
      ].filter((field): field is IInvoiceOverviewField => field !== null);

    case InvoiceStatus.PartialApproval:
      return [
        ...fields,
        afterReviewField(invoice.reviewedAmount ?? 0, savings, currencyCode),
        downloadField,
        rejectionCategoryField,
        reasonField,
      ];

    default:
      console.warn(`Unknown invoice status: ${invoice.status}`);
      return [];
  }
};

const renderLinkOrTextField = (field: IInvoiceOverviewField) => {
  if (field.value === undefined && field.defaultValue === undefined)
    return <Skeleton width={'75%'} />;

  switch (field.type) {
    case 'text':
      return <Typography variant="body2">{field.value || field.defaultValue}</Typography>;
    case 'link':
      return (
        <LiftAITextLink
          variant="body2"
          label={(field.value || field.defaultValue) ?? ''}
          href={field.uri}
        />
      );
    case 'custom':
      return <field.element />;
    default:
      return null;
  }
};

/** Two across in both layouts, fixed so the pairs stay put rather than reflowing onto their own rows on a wider window. */
const FIELD_SIZES: Record<InvoiceOverviewLayout, Record<string, number>> = {
  drawer: { xs: 6 },
  page: { xs: 12, sm: 6 },
};

/** Where the Decision block is rendered. `header` is the detail page's right-hand column, a fraction of the width the other two get. */
export type DecisionLayout = InvoiceOverviewLayout | 'header' | 'compact';

const DECISION_FIELD_SIZES: Record<DecisionLayout, Record<string, number>> = {
  drawer: { xs: 6 },
  page: { xs: 12, sm: 6, md: 3 },
  header: { xs: 6 },
  compact: { xs: 12 },
};

const renderFieldAndGrid = (
  field: IInvoiceOverviewField,
  index: number,
  sizes: Record<string, number> = FIELD_SIZES.drawer,
  dense = false,
) => {
  return (
    <Grid size={{ ...(field.fullWidth ? { xs: 12 } : sizes) }} key={index} my={dense ? 1 : 2}>
      <Typography variant="body2" color="text.secondary">
        {field.label}
      </Typography>
      {/* A rejection reason runs to a paragraph, so it is clamped in the header's column
          and the full text stays available on hover. */}
      {dense && field.type === 'text' ? (
        <Box
          title={field.value ?? undefined}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {renderLinkOrTextField(field)}
        </Box>
      ) : (
        renderLinkOrTextField(field)
      )}
      {field.caption ? (
        <Typography component="div" variant="caption" color="text.secondary">
          {field.caption}
        </Typography>
      ) : null}
    </Grid>
  );
};

const CompactDecisionBand = ({
  invoice,
  currencyCode,
  color,
  action,
}: {
  invoice: Invoice;
  currencyCode: string;
  color: NonNullable<ReturnType<typeof invoiceStatusToColorMap.get>>;
  action?: ReactNode;
}) => {
  const shown = new Set(getDecisionFields(invoice, currencyCode).map((field) => field.name));

  const stampedLine = [
    invoice.dateStamped ? toLongDateFormat(invoice.dateStamped) : null,
    invoice.consultant?.fullName,
  ]
    .filter(Boolean)
    .join(' · ');

  const afterReview = !shown.has('reviewedAmount')
    ? null
    : invoice.status === InvoiceStatus.Rejected
      ? 0
      : (invoice.reviewedAmount ?? 0);
  const savings = invoice.savings ?? 0;
  const categories = (shown.has('rejectionCategory') ? (invoice.rejectionCategory ?? []) : [])
    .map((category) => invoiceRejectionCategoryToLabelMap.get(category))
    .filter(Boolean);
  const reason = shown.has('reason') ? invoice.reason : null;

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1} rowGap={0.5}>
        <Typography variant="subtitle2" fontWeight={600}>
          Decision
        </Typography>
        <Chip
          label={invoiceStatusToLabelMap.get(invoice.status) ?? 'unknown'}
          size="small"
          color={color}
        />
        {stampedLine ? (
          <Typography variant="caption" color="text.secondary">
            {stampedLine}
          </Typography>
        ) : null}
      </Stack>
      {afterReview !== null || categories.length > 0 ? (
        <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1} rowGap={0.5}>
          {afterReview !== null ? (
            <Typography variant="body2">
              {formatCurrency({ value: afterReview, currencyCode })} after review
              {savings ? (
                <Typography component="span" variant="body2" color="success.main">
                  {` · ${formatCurrency({ value: savings, currencyCode })} saved`}
                </Typography>
              ) : null}
            </Typography>
          ) : null}
          {categories.map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined" />
          ))}
        </Stack>
      ) : null}
      {reason ? (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          title={reason}
          sx={{
            fontStyle: 'italic',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          “{reason}”
        </Typography>
      ) : null}
      {shown.has('stampedInvoice') || action ? (
        <Stack direction="row" alignItems="center" gap={1}>
          {shown.has('stampedInvoice') ? (
            <DownloadStampedInvoiceDropdownMenu invoice={invoice} outlined />
          ) : null}
          {action}
        </Stack>
      ) : null}
    </Stack>
  );
};

/**
 * The consultant's final call on a finalized invoice, tinted and bordered in the
 * status colour. On the detail page it takes the header's right-hand column, so the
 * call and the way to undo it are in one spot whatever tab is open.
 */
export const DecisionSection = ({
  invoice,
  currencyCode,
  layout,
  action,
}: {
  invoice: Invoice;
  currencyCode: string;
  layout: DecisionLayout;
  action?: ReactNode;
}) => {
  const color = invoiceStatusToColorMap.get(invoice.status) ?? 'info';
  const compact = layout === 'compact';
  const dense = layout === 'header';

  return (
    <Box
      sx={{
        // A status card: same shape as CARD_SX, but tinted to its status rather than the
        // neutral `divider`.
        ...CARD_SX,
        p: compact ? 1.5 : 2,
        borderColor: `${color}.light`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.06),
      }}
    >
      {compact ? (
        <CompactDecisionBand
          invoice={invoice}
          currencyCode={currencyCode}
          color={color}
          action={action}
        />
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Decision
            </Typography>
            <Chip
              label={invoiceStatusToLabelMap.get(invoice.status) ?? 'unknown'}
              size="small"
              color={color}
            />
          </Stack>
          <Grid container columnSpacing={2}>
            {getDecisionFields(invoice, currencyCode).map((field, index) =>
              renderFieldAndGrid(field, index, DECISION_FIELD_SIZES[layout], dense),
            )}
          </Grid>
          {action ? <Box mt={dense ? 1 : 2}>{action}</Box> : null}
        </>
      )}
    </Box>
  );
};

/** Exported for the full-page detail view, which has no Overview tab: the notice moved to the top of its header. */
export const AdjustedInvoiceBanner = ({ invoice }: { invoice: Invoice }) => {
  if (!invoice.parentInvoice) return null;

  const url = buildUrlForInvoices({
    invoiceId: invoice.parentInvoice.id,
    kind: InvoiceKind.Invoice,
  });

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2">
        This is an adjusted version of Invoice{' '}
        <LiftAITextLink label={`#${invoice.parentInvoice.number}`} href={url} />
      </Typography>
    </Alert>
  );
};

export const AdjustmentChainSection = ({
  invoice,
  adjustments,
  variant = 'card',
}: {
  invoice: Invoice;
  adjustments: AdjustmentInvoice[];
  variant?: 'card' | 'field';
}) => {
  const { currencyCode } = useContext(UserContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [adjustedUploadDocs, setAdjustedUploadDocs] = useState<LiftAIDocumentUntagged[] | null>(
    null,
  );

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const attachment = await uploadAttachment({
        file,
        name: file.name,
        fileType: 'pdf',
      });

      const pdfDoc = await pdfjs.getDocument(attachment.file).promise;
      const documents: LiftAIDocumentUntagged[] = new Array(pdfDoc.numPages)
        .fill(0)
        .map((_, i) => ({
          fileType: 'pdf' as const,
          name: attachment.name ?? '',
          file: pdfDoc,
          pageNum: i + 1,
          attachment,
        }));
      setAdjustedUploadDocs(documents);
    } finally {
      setIsUploading(false);
      // Reset file input so picking the same file again triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  const handleUploadDone = useCallback(
    (_taggedDocs: LiftAIDocumentTagged[]) => {
      setAdjustedUploadDocs(null);
      // Refresh invoice data so the new adjustment appears
      void mutate(getInvoiceMutator(invoice.id));
      // Refresh dispute timeline to show the "adjusted invoice linked" entry
      void mutate(['dispute', invoice.id, 'timeline']);
      void mutate(['dispute', invoice.id]);
    },
    [invoice.id],
  );

  // Declared once and rendered by whichever variant is in play — `initialFormData` is
  // what links the upload back to this invoice, so two copies would be two things to
  // keep in step.
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="application/pdf"
      hidden
      onChange={handleFileChange}
    />
  );

  const uploadDialog = adjustedUploadDocs ? (
    <SelectUploadDocumentTypeDialog
      open
      onCancel={() => setAdjustedUploadDocs(null)}
      onDone={handleUploadDone}
      documents={adjustedUploadDocs}
      initialDocumentType={UploadDocumentType.invoice}
      initialFormData={{
        parentInvoiceId: invoice.id,
        adjustmentSource: 'manual' as const,
        propertyId: invoice.property?.id,
      }}
    />
  ) : null;

  const uploadAction = (
    <>
      {fileInput}
      <Button
        variant="outlined"
        color="primary"
        size="small"
        startIcon={<AddIcon />}
        loading={isUploading}
        onClick={() => fileInputRef.current?.click()}
        sx={BUTTON_SX}
      >
        Upload Adjusted Invoice
      </Button>
    </>
  );

  if (variant === 'field') {
    return (
      <>
        <HeaderField
          label="Adjusted Invoices"
          caption={
            <Button
              size="small"
              startIcon={<AddIcon />}
              loading={isUploading}
              onClick={() => fileInputRef.current?.click()}
              sx={{ ...BUTTON_SX, ml: -0.5, p: 0, minWidth: 0, fontSize: 'inherit' }}
            >
              Upload
            </Button>
          }
        >
          {adjustments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={1}>
              {adjustments.map((adjustment) => (
                <Typography variant="body2" component="span" key={adjustment.id}>
                  <LiftAITextLink
                    label={`#${adjustment.number}`}
                    href={buildUrlForInvoices({
                      invoiceId: adjustment.id,
                      kind: InvoiceKind.Invoice,
                    })}
                  />
                </Typography>
              ))}
            </Stack>
          )}
        </HeaderField>
        {fileInput}
        {uploadDialog}
      </>
    );
  }

  return (
    <>
      <OverviewCard
        title="Adjusted Invoices"
        chip={
          adjustments.length > 0 ? (
            <Chip label={adjustments.length} size="small" variant="outlined" color="primary" />
          ) : undefined
        }
        action={uploadAction}
      >
        {adjustments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No adjusted invoices.
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            {adjustments.map((adjustment) => {
              const url = buildUrlForInvoices({
                invoiceId: adjustment.id,
                kind: InvoiceKind.Invoice,
              });

              return (
                <Stack
                  key={adjustment.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  py={1}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" component="span">
                      <LiftAITextLink label={adjustment.number} href={url} />
                    </Typography>
                    <Chip
                      label={invoiceStatusToLabelMap.get(adjustment.status) ?? 'unknown'}
                      size="small"
                      variant="outlined"
                      color={invoiceStatusToColorMap.get(adjustment.status) ?? 'primary'}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency({ value: adjustment.proposedAmount ?? null, currencyCode })}
                    {adjustment.savingsDelta != null
                      ? ` · ${formatCurrency({ value: adjustment.savingsDelta, currencyCode })} savings`
                      : ''}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
      </OverviewCard>
      {uploadDialog}
    </>
  );
};
